import { Filter, Search } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { StrikePill } from './StrikePill'

interface StrikeSelectionPanelProps {
    title: string
    strikes: number[]
    selectedStrikes: number[]
    onToggle: (strike: number) => void
    onSelectAll: () => void
    onClear: () => void
    onSelectRange: (range: 'ATM' | 'ITM' | 'OTM') => void
    colorTheme: 'emerald' | 'rose'
}

export const StrikeSelectionPanel: React.FC<StrikeSelectionPanelProps> = ({
    title,
    strikes,
    selectedStrikes,
    onToggle,
    onSelectAll,
    onClear,
    onSelectRange,
    colorTheme,
}) => {
    const [search, setSearch] = useState('')
    const filteredStrikes = strikes.filter((s) => s.toString().includes(search))

    const themeClasses =
        colorTheme === 'emerald'
            ? {
                  border: 'border-emerald-100 dark:border-emerald-800',
                  bg: 'bg-emerald-50/30 dark:bg-emerald-900/10',
                  badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
              }
            : {
                  border: 'border-rose-100 dark:border-rose-800',
                  bg: 'bg-rose-50/30 dark:bg-rose-900/10',
                  badge: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300',
              }

    return (
        <div
            className={`flex flex-col h-[300px] min-h-[250px] rounded-2xl border ${themeClasses.border} ${themeClasses.bg} shadow-sm overflow-hidden relative transition-all duration-300 bg-white dark:bg-slate-900`}
        >
            {/* Unified Top Bar */}
            <div className="px-4 py-3 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 flex items-center gap-3 shrink-0 transition-colors duration-300">
                <span
                    className={`px-2.5 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider shrink-0 shadow-sm border border-transparent ${themeClasses.badge}`}
                >
                    {title.includes('Call') ? 'CE Calls' : 'PE Puts'}
                </span>

                {/* Compact Search */}
                <div className="flex-1 relative group">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 group-focus-within:text-primary dark:group-focus-within:text-blue-400 transition-colors"
                    />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-primary/50 dark:focus:border-blue-500/50 text-slate-800 dark:text-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs font-semibold placeholder:text-slate-400 dark:placeholder:text-slate-600 outline-none transition-all shadow-sm focus:shadow-md focus:shadow-primary/5 dark:focus:shadow-blue-500/10"
                        placeholder={`Search ${title.includes('Call') ? 'CE' : 'PE'}...`}
                    />
                </div>

                <div className="text-[10px] font-mono font-bold text-slate-400 dark:text-slate-500 min-w-[24px] text-right bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-md border border-slate-100 dark:border-slate-700">
                    {selectedStrikes.length}
                </div>
            </div>

            {/* Quick Filters Toolbar */}
            <div className="px-3 py-2 bg-white/50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-800 flex flex-wrap gap-2 shrink-0 justify-between items-center backdrop-blur-sm transition-colors duration-300">
                <div className="flex gap-2 overflow-x-auto no-scrollbar mask-gradient-right">
                    <button
                        onClick={() => onSelectRange('ATM')}
                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
                    >
                        Nr ATM
                    </button>
                    <button
                        onClick={() => onSelectRange('ITM')}
                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
                    >
                        ITM
                    </button>
                    <button
                        onClick={() => onSelectRange('OTM')}
                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
                    >
                        OTM
                    </button>
                </div>
                <div className="flex gap-2 pl-2 border-l border-gray-200/60 dark:border-slate-700/60">
                    <button
                        onClick={onSelectAll}
                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border border-blue-100 dark:border-blue-800"
                    >
                        All
                    </button>
                    <button
                        onClick={onClear}
                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-transparent text-slate-400 dark:text-slate-500 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Grid Area */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-white/30 dark:bg-slate-900/30">
                {filteredStrikes.length > 0 ? (
                    <div className="flex flex-wrap gap-2 content-start">
                        {filteredStrikes.map((strike) => (
                            <StrikePill
                                key={strike}
                                strike={strike}
                                selected={selectedStrikes.includes(strike)}
                                onToggle={() => onToggle(strike)}
                                colorTheme={colorTheme}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300 dark:text-slate-600">
                        <Filter size={32} className="mb-3 opacity-30" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">No Strikes Matching</span>
                    </div>
                )}
            </div>
        </div>
    )
}
